import { EntityCard } from "./EntityCard";
import { EntityTypeBadge } from "./entityTypeMeta";
import { Badge } from "../../base/badge";
import { ChevronRight, Mail, Phone } from "lucide-react";
import { cn } from "@charphq/ui/lib/utils";

interface Customer {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  isActive?: boolean;
}

interface CustomerCardProps {
  customer: Customer;
  onClick?: () => void;
  className?: string;
  /** Highlight the card as the active keyboard selection. */
  isSelected?: boolean;
}

export function CustomerCard({ customer, onClick, className, isSelected }: CustomerCardProps) {
  return (
    <EntityCard
      onClick={onClick}
      className={cn(className, isSelected && "border-primary ring-2 ring-primary/50 shadow-md")}
    >
      <div className="flex h-full items-center justify-between">
        {/* Left side - Main content */}
        <div className="flex-1 min-w-0">
          {/* Top row: type badge, name, status */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <EntityTypeBadge kind="customer" />
              <h3 className="font-medium text-base truncate">{customer.name}</h3>
            </div>
            {customer.isActive !== undefined && (
              <Badge
                variant={customer.isActive ? "default" : "secondary"}
                className="text-xs shrink-0 ml-2"
              >
                {customer.isActive ? "Active" : "Inactive"}
              </Badge>
            )}
          </div>

          {/* Second row: email and phone */}
          {(customer.email || customer.phone) && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {customer.email && (
                <div className="flex items-center gap-1 min-w-0">
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate">{customer.email}</span>
                </div>
              )}
              {customer.phone && (
                <div className="flex items-center gap-1 shrink-0">
                  <Phone className="h-3 w-3" />
                  <span>{customer.phone}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right side - Chevron */}
        <div className="flex flex-col items-end gap-2 shrink-0 ml-4">
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </EntityCard>
  );
}
